import { useEffect, useRef } from 'react';
import { useComms } from './context';

/**
 * Re-read this view's own data when the module-wide Refresh is pressed.
 *
 * The button in the tab bar can reload the module's catalogs itself, but not
 * the conversation list, the open thread, the call log or the campaign table —
 * those live in the views that draw them. So the bar bumps a nonce and each
 * holder of a load answers for itself. A view that forgets this hook is the one
 * part of the screen a refresh silently misses, which is why the hook is the
 * whole contract: one line per load, and nothing to get subtly wrong.
 *
 * Never fires on mount — the view has just loaded — and deliberately does not
 * depend on `reload`, whose identity changes with the source. Depending on it
 * would re-fetch on a change the refresh had nothing to do with.
 */
export function useModuleRefresh(reload: () => void): void {
  const { refreshNonce } = useComms();
  const seen = useRef(refreshNonce);
  const latest = useRef(reload);
  useEffect(() => {
    latest.current = reload;
  });
  useEffect(() => {
    if (refreshNonce === seen.current) return;
    seen.current = refreshNonce;
    latest.current();
  }, [refreshNonce]);
}
